import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Autocomplete,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Search,
  FilterList,
  Person,
  Business,
  Email as EmailIcon,
  Security,
  AdminPanelSettings,
  Visibility,
  VisibilityOff,
  UploadFile
} from '@mui/icons-material';
import { listUsers, createUser, updateUser, deleteUser } from '../../services/users';
import { AuthContext } from '../../contexts/AuthContext';
import ImportUsersModal from '../../components/super-admin/ImportUsersModal';

const THEME = {
  primary: '#0DA1B8',
  primaryDark: '#0C587D',
  primaryLight: '#3BC5D9',
  primaryUltraLight: '#F0FDFA',
  accent: '#00B4DB',
  accentLight: '#E0F2FE',
  accentUltraLight: '#F8FAFC',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  surfaceElevated: '#FFFFFF',
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  textTertiary: '#94A3B8',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  gradientPrimary: 'linear-gradient(135deg, #0083B0 0%, #00B4DB 100%)',
  gradientAccent: 'linear-gradient(135deg, #0DA1B8 0%, #0C587D 100%)',
  gradientSuperAdmin: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  hover: '#F0FDFA'
};

const ROLE_OPTS = [
  { value: 'super_admin', label: 'Super Admin (Global Administrator)' },
  { value: 'dealer_admin', label: 'Service Manager (Dealer Admin)' },
  { value: 'branch_admin', label: 'Branch Admin' },
  { value: 'dealer_user', label: 'Service Advisor (Dealer User)' }
];

export default function UserManagement() {
  const { user: authUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    role: 'super_admin',
    password: '',
    dealer_id: '',
    showroom_name: '',
    is_active: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('username');
  const [order, setOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [availableDealers, setAvailableDealers] = useState([]);

  const currentAuthUsername = authUser?.username || authUser?.sub || '';
  const currentAuthId = authUser?.user_id || authUser?._id || authUser?.id || '';

  const isCurrentLoggedInUser = (targetUser) => {
    if (!targetUser) return false;
    const targetId = targetUser._id || targetUser.id;
    return (
      (currentAuthUsername && targetUser.username && currentAuthUsername.toLowerCase() === targetUser.username.toLowerCase()) ||
      (currentAuthId && targetId && currentAuthId === targetId)
    );
  };

  const handleToggleStatus = async (user) => {
    if (isCurrentLoggedInUser(user)) {
      alert('You cannot deactivate your own logged-in account.');
      return;
    }
    const userId = user._id || user.id;
    const currentActive = user.is_active !== false && user.status !== 'inactive';
    try {
      await updateUser(userId, { is_active: !currentActive, status: !currentActive ? 'active' : 'inactive' });
      load();
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update status');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      const userList = Array.isArray(data) ? data : [];

      const REGISTERED_ACTIVE_DEALERS = [
        { id: 'BIRD', name: 'BIRD' },
        { id: 'BMW-KUN', name: 'BMW-KUN' },
        { id: 'DEUTSCHEMOTOREN', name: 'DEUTSCHEMOTOREN' },
        { id: 'EMINENT', name: 'EMINENT' },
        { id: 'EVMAUTOKRAFT', name: 'EVMAUTOKRAFT' }
      ];
      setAvailableDealers(REGISTERED_ACTIVE_DEALERS);

      // Load all system users including super_admin, dealer_admin, branch_admin, dealer_user
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!open) {
      setForm({
        username: '',
        email: '',
        role: 'super_admin',
        password: '',
        dealer_id: '',
        showroom_name: '',
        is_active: true
      });
      setEditingUser(null);
      setError('');
    }
  }, [open]);

  const handleCreate = async () => {
    setError('');
    if (!form.username || !form.email || !form.password) {
      setError('Username, email, and password are required');
      return;
    }
    if (form.role === 'dealer_admin' && !form.dealer_id) {
      setError('Dealer ID is required for Dealer Admins to link data');
      return;
    }
    if (form.role !== 'super_admin' && !form.showroom_name) {
      setError('Showroom name is required for dealer accounts');
      return;
    }

    try {
      const payload = {
        ...form,
        dealer_id: form.role === 'super_admin' ? (form.dealer_id || null) : form.dealer_id,
        showroom_name: form.role === 'super_admin' ? (form.showroom_name || 'CitNow Headquarters') : form.showroom_name
      };
      await createUser(payload);
      setOpen(false);
      load();
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.detail || error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    setError('');

    if (!form.username || !form.email) {
      setError('Username and email are required');
      return;
    }
    if (form.role !== 'super_admin' && !form.showroom_name) {
      setError('Showroom name is required for dealer accounts');
      return;
    }

    try {
      const updateData = { ...form };
      if (!updateData.password) {
        delete updateData.password;
      }
      if (updateData.dealer_id === '' || updateData.role === 'super_admin') {
        updateData.dealer_id = updateData.dealer_id || null;
      }
      if (updateData.role === 'super_admin' && !updateData.showroom_name) {
        updateData.showroom_name = 'CitNow Headquarters';
      }

      await updateUser(editingUser._id || editingUser.id, updateData);
      setOpen(false);
      load();
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.detail || error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDelete = async (targetUser) => {
    if (isCurrentLoggedInUser(targetUser)) {
      alert('You cannot delete your own logged-in account.');
      return;
    }
    const targetId = targetUser._id || targetUser.id;
    const roleLabel = targetUser.role === 'super_admin' ? 'Super Admin' : targetUser.role;
    if (window.confirm(`Are you sure you want to delete administrator "${targetUser.username}" (${roleLabel})?`)) {
      try {
        await deleteUser(targetId);
        load();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'dealer_admin',
      password: '',
      dealer_id: user.dealer_id || '',
      showroom_name: user.showroom_name || '',
      is_active: user.is_active !== false && user.status !== 'inactive'
    });
    setOpen(true);
  };

  const handleSubmit = () => {
    if (editingUser) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      (user.username || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower) ||
      (user.dealer_id || '').toLowerCase().includes(searchLower) ||
      (user.showroom_name || '').toLowerCase().includes(searchLower);

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aValue = a[orderBy] || '';
    const bValue = b[orderBy] || '';

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getRoleBadge = (role, userObj = null) => {
    if (role === 'master_admin' || userObj?.is_master_admin || userObj?.username === 'tech@focus') {
      return {
        label: 'Master Admin (Tech)',
        color: '#6D28D9',
        bg: '#EDE9FE',
        border: '#DDD6FE',
        icon: <Security sx={{ fontSize: '13px !important' }} />
      };
    }
    switch (role) {
      case 'super_admin':
        return {
          label: 'Super Admin',
          color: '#B45309',
          bg: '#FEF3C7',
          border: '#FDE68A',
          icon: <Security sx={{ fontSize: '13px !important' }} />
        };
      case 'dealer_admin':
        return {
          label: 'Service Manager',
          color: '#0369A1',
          bg: '#E0F2FE',
          border: '#BAE6FD',
          icon: <Business sx={{ fontSize: '13px !important' }} />
        };
      case 'branch_admin':
        return {
          label: 'Branch Admin',
          color: '#4338CA',
          bg: '#EEF2FF',
          border: '#C7D2FE',
          icon: <Person sx={{ fontSize: '13px !important' }} />
        };
      case 'dealer_user':
        return {
          label: 'Service Advisor',
          color: '#047857',
          bg: '#D1FAE5',
          border: '#A7F3D0',
          icon: <Person sx={{ fontSize: '13px !important' }} />
        };
      default:
        return {
          label: role || 'User',
          color: '#475569',
          bg: '#F1F5F9',
          border: '#E2E8F0',
          icon: <Person sx={{ fontSize: '13px !important' }} />
        };
    }
  };

  const isEditMode = Boolean(editingUser);
  const dialogTitle = isEditMode
    ? (form.role === 'super_admin' ? 'Edit Super Admin' : 'Edit Administrator')
    : (form.role === 'super_admin' ? 'Create New Super Admin' : 'Create New Administrator');
  const submitButtonText = isEditMode ? 'Update Account' : 'Create Account';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDFA 100%)',
      py: 2.5,
      px: { xs: 2, md: 3 },
      width: '100%'
    }}>
      <Container maxWidth={false} disableGutters>
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 1.5
          }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: THEME.textPrimary,
                  mb: 0.5,
                  fontSize: '1.65rem',
                  background: THEME.gradientPrimary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Administrator & User Management
              </Typography>
              <Typography variant="body2" sx={{ color: THEME.textSecondary, fontSize: '0.875rem' }}>
                Manage Super Admins, Dealer Service Managers, Branch Admins, and Service Advisors
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<UploadFile />}
                onClick={() => setImportModalOpen(true)}
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  borderColor: THEME.primary,
                  color: THEME.primary,
                  '&:hover': {
                    background: THEME.primaryUltraLight,
                    borderColor: THEME.primaryDark,
                  }
                }}
              >
                Import from Excel
              </Button>
              <Button
                variant="contained"
                size="medium"
                startIcon={<Add />}
                onClick={() => {
                  setForm({
                    username: '',
                    email: '',
                    role: 'super_admin',
                    password: '',
                    dealer_id: '',
                    showroom_name: 'CitNow Headquarters',
                    is_active: true
                  });
                  setEditingUser(null);
                  setOpen(true);
                }}
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  background: THEME.gradientPrimary,
                  boxShadow: THEME.shadowMd,
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(13, 161, 184, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                + New Administrator
              </Button>
            </Box>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 4,
            border: `1px solid ${THEME.border}`,
            borderRadius: 3,
            overflow: 'hidden',
            background: '#FFFFFF'
          }}
        >
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(13, 161, 184, 0.05) 0%, rgba(12, 88, 125, 0.05) 100%)',
            p: 3
          }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                placeholder="Search by username, email, showroom, or dealer ID..."
                size="medium"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: THEME.primary }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: THEME.primary
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: THEME.primary,
                      borderWidth: 2
                    }
                  }
                }}
              />

              <TextField
                select
                size="medium"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: THEME.primary
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterList sx={{ color: THEME.primary }} />
                    </InputAdornment>
                  )
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
                <MenuItem value="dealer_admin">Service Manager</MenuItem>
                <MenuItem value="branch_admin">Branch Admin</MenuItem>
                <MenuItem value="dealer_user">Service Advisor</MenuItem>
              </TextField>
            </Stack>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: THEME.primary }} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Paper elevation={0} sx={{ p: 8, textAlign: 'center', border: `1px solid ${THEME.border}` }}>
            <Person sx={{ fontSize: 64, color: THEME.border, mb: 2 }} />
            <Typography variant="h6" sx={{ color: THEME.textSecondary, mb: 1 }}>
              {searchQuery || roleFilter !== 'all' ? 'No users found' : 'No administrators yet'}
            </Typography>
            <Typography variant="body2" sx={{ color: THEME.textSecondary, mb: 3 }}>
              {searchQuery || roleFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first administrator to get started'}
            </Typography>
            {!searchQuery && roleFilter === 'all' && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setForm({
                    username: '',
                    email: '',
                    role: 'super_admin',
                    password: '',
                    dealer_id: '',
                    showroom_name: 'CitNow Headquarters',
                    is_active: true
                  });
                  setEditingUser(null);
                  setOpen(true);
                }}
                sx={{ background: THEME.gradientPrimary }}
              >
                Create Administrator
              </Button>
            )}
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${THEME.border}`,
              borderRadius: 3,
              overflow: 'hidden',
              background: '#FFFFFF'
            }}
          >
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(135deg, rgba(13, 161, 184, 0.08) 0%, rgba(12, 88, 125, 0.08) 100%)',
                    '& th': { py: 1.2, px: 1.2 }
                  }}>
                    <TableCell sx={{ width: '22%' }}>
                      <TableSortLabel
                        active={orderBy === 'username'}
                        direction={orderBy === 'username' ? order : 'asc'}
                        onClick={() => handleRequestSort('username')}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          Username
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          Email
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Role
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <TableSortLabel
                        active={orderBy === 'showroom_name'}
                        direction={orderBy === 'showroom_name' ? order : 'asc'}
                        onClick={() => handleRequestSort('showroom_name')}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          Showroom Name
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '10%' }}>
                      <TableSortLabel
                        active={orderBy === 'dealer_id'}
                        direction={orderBy === 'dealer_id' ? order : 'asc'}
                        onClick={() => handleRequestSort('dealer_id')}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          Dealer ID
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '10%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Status (Login)
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: '5%', pr: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => {
                    const isActiveUser = user.is_active !== false && user.status !== 'inactive';
                    const isSelf = isCurrentLoggedInUser(user);
                    const isSuperAdminRole = user.role === 'super_admin';
                    const badge = getRoleBadge(user.role, user);

                    return (
                      <TableRow
                        key={user._id || user.id}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(13, 161, 184, 0.04)'
                          },
                          borderBottom: `1px solid ${THEME.borderLight}`,
                          '& td': { py: 1.2, px: 1.5 }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background: isSuperAdminRole ? THEME.gradientSuperAdmin : THEME.gradientPrimary,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                boxShadow: isSuperAdminRole ? '0 2px 6px rgba(245, 158, 11, 0.3)' : '0 2px 6px rgba(13, 161, 184, 0.2)',
                                flexShrink: 0
                              }}
                            >
                              {user.username.charAt(0).toUpperCase()}
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: THEME.textPrimary, fontSize: '0.85rem' }}>
                                {user.username}
                                {isSelf && (
                                  <Chip
                                    label="You"
                                    size="small"
                                    sx={{ ml: 0.8, height: 18, fontSize: '0.625rem', bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 700 }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: THEME.textSecondary, fontSize: '0.825rem' }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={badge.icon}
                            label={badge.label}
                            size="small"
                            sx={{
                              bgcolor: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              height: 24,
                              '& .MuiChip-icon': { color: `${badge.color} !important` }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: THEME.textPrimary, fontSize: '0.85rem' }}>
                            {isSuperAdminRole ? (user.showroom_name || 'CitNow Headquarters') : (user.showroom_name || '—')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isSuperAdminRole && !user.dealer_id ? (
                            <Chip
                              label="Global"
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.675rem', fontWeight: 600, color: '#D97706', borderColor: '#FDE68A', bgcolor: '#FFFBEB' }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{
                              fontFamily: 'monospace',
                              color: user.dealer_id ? THEME.textPrimary : THEME.textSecondary,
                              fontSize: '0.825rem',
                              fontWeight: 600
                            }}>
                              {user.dealer_id || '—'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={isSelf ? "You cannot deactivate your own account" : isActiveUser ? "Click to Deactivate (Disables Login)" : "Click to Activate (Enables Login)"}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                              <Switch
                                size="small"
                                checked={isActiveUser}
                                disabled={isSelf}
                                onChange={() => handleToggleStatus(user)}
                                color="success"
                              />
                              <Chip
                                label={isActiveUser ? 'Active' : 'Inactive'}
                                size="small"
                                color={isActiveUser ? 'success' : 'error'}
                                variant="outlined"
                                sx={{ fontWeight: 700, fontSize: '0.675rem', height: 20 }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 2, whiteSpace: 'nowrap' }}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit user profile">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(user)}
                                sx={{
                                  color: THEME.primary,
                                  '&:hover': { bgcolor: `${THEME.primary}10` }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isSelf && (
                              <Tooltip title="Delete user">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(user)}
                                  sx={{
                                    color: THEME.error,
                                    '&:hover': { bgcolor: `${THEME.error}10` }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: THEME.shadowMd }
          }}
        >
          <DialogTitle sx={{
            bgcolor: THEME.surface,
            borderBottom: `1px solid ${THEME.border}`,
            fontWeight: 600,
            color: THEME.textPrimary
          }}>
            {dialogTitle}
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Stack spacing={2.5}>
              <TextField
                fullWidth
                select
                label="Account Role"
                value={form.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setForm({
                    ...form,
                    role: newRole,
                    showroom_name: newRole === 'super_admin' ? (form.showroom_name || 'CitNow Headquarters') : form.showroom_name
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {form.role === 'super_admin' ? (
                        <AdminPanelSettings sx={{ color: '#D97706' }} />
                      ) : (
                        <Security sx={{ color: THEME.textSecondary }} />
                      )}
                    </InputAdornment>
                  )
                }}
                helperText={form.role === 'super_admin' ? "Super Admins have full access across all dealerships, users, and reports" : ""}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                {ROLE_OPTS.map(r => (
                  <MenuItem key={r.value} value={r.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {r.value === 'super_admin' ? <Security sx={{ fontSize: 16, color: '#D97706' }} /> : <Person sx={{ fontSize: 16, color: THEME.primary }} />}
                      {r.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: THEME.textSecondary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: THEME.textSecondary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                label="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                helperText={isEditMode && !form.password ? "Leave blank to keep current password" : "Required for new users (min 6 characters)"}
                required={!isEditMode && !form.password}
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Autocomplete
                freeSolo
                options={availableDealers}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option.toUpperCase();
                  return option.id.toUpperCase();
                }}
                value={availableDealers.find(d => d.id.toLowerCase() === (form.dealer_id || '').toLowerCase()) || (form.dealer_id ? form.dealer_id.toUpperCase() : '')}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    const uppercaseVal = newValue.toUpperCase();
                    setForm({ ...form, dealer_id: uppercaseVal, showroom_name: form.showroom_name || uppercaseVal });
                  } else if (newValue && newValue.id) {
                    setForm({ ...form, dealer_id: newValue.id, showroom_name: newValue.name || newValue.id });
                  } else {
                    setForm({ ...form, dealer_id: '' });
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue) {
                    setForm({ ...form, dealer_id: newInputValue.toUpperCase() });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={form.role === 'super_admin' ? "Dealer ID (Optional)" : "Dealer ID"}
                    required={form.role === 'dealer_admin'}
                    helperText={form.role === 'super_admin' ? "Optional: Leave blank for global Super Admin access" : form.role === 'dealer_admin' ? "Select existing dealer or type a new ID" : "Optional: Assign to specific dealership"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 1 }}>
                            <Business sx={{ color: THEME.textSecondary }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />

              <TextField
                fullWidth
                label={form.role === 'super_admin' ? "Showroom / Organization Name (Optional)" : "Showroom Name"}
                value={form.showroom_name}
                onChange={(e) => setForm({ ...form, showroom_name: e.target.value })}
                required={form.role !== 'super_admin'}
                helperText={form.role === 'super_admin' ? "Defaults to 'CitNow Headquarters' if left empty" : "Enter the dealership/showroom name"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: THEME.textSecondary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Box sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${THEME.borderLight}`,
                backgroundColor: THEME.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active !== false}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: THEME.textPrimary }}>
                        Account Status: {form.is_active !== false ? 'ACTIVE' : 'INACTIVE'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: THEME.textSecondary, display: 'block' }}>
                        {form.is_active !== false ? 'User can log in and access system functions' : 'Login disabled for this account'}
                      </Typography>
                    </Box>
                  }
                />
                <Chip
                  label={form.is_active !== false ? 'Active' : 'Inactive'}
                  color={form.is_active !== false ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, bgcolor: THEME.surface }}>
            <Button
              onClick={() => setOpen(false)}
              sx={{ color: THEME.textSecondary, fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: form.role === 'super_admin' ? THEME.gradientSuperAdmin : THEME.gradientPrimary,
                '&:hover': { opacity: 0.9 },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 4
              }}
            >
              {submitButtonText}
            </Button>
          </DialogActions>
        </Dialog>

        <ImportUsersModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => load()}
        />
      </Container>
    </Box>
  );
}
